import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  addCrimeReferences,
  editCrimeReferences,
  fetchCrimeReferenceReceptionMethod,
} from "services/incidentService";
import {
  CrimeReference,
  CrimeReferenceReceptionMethod,
  CrimeType,
  InvestigationFile,
  ReferenceType,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  incident?: CrimeReference;
  file?: InvestigationFile | null;
};

const defaultIncident: CrimeReference = {
  uu_id: 0,

  // Crime Information
  crime_reference_number: "",
  date_received: null,
  crime_type: null,
  reception_method: null,
  category: null,
  receiving_prosecutor: "",
  receiving_police: null,
  description: null,
  status: "pending",

  // Incident Information
  year: "",
  crime_incidence_time: "",
  incident_timestamp: "",
  crime_incident_narrative: "",
  victim_status: "",
  crime_category: "",
  evidence_written: "",
  tools_used: "",

  // Address Information
  crime_incidence_region: "",
  crime_incidence_zone: "",
  crime_incidence_city: "",
  crime_incidence_woreda: "",
  crime_incidence_kebele: "",
  crime_incidence_site: "",

  // Decision Information
  screening_outcome: null,
  follow_up_action: "",
  decision_type: null,
  decision_date: null,

  screening_team: [],
  victims: [],
  suspects: [],
  witnesses: [],
  Incident_Registration_number: null,
  reference_type: null,
  asset_reference: "",
  decision_details: "",
  crime_naretive_file: null,
  is_letter: false,
  is_efling: false,
};

const IncidentInformationTab: React.FC<Props> = ({ incident, file }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [tab, setTab] = useState("Crime Information");
  const [receptionMethods, setReceptionMethods] = useState<
    CrimeReferenceReceptionMethod[]
  >([]);
  // const [prosecutors, setProsecutor] = useState<Prosecutor[]>([]);
  // const [polices, setPolices] = useState<Police[]>([]);
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<CrimeReference>(defaultIncident);

  // const [baseData, setBaseData] = useState<{
  //   crimeCategory: CrimeCategoryBaseData[];
  // }>({
  //   crimeCategory: [],
  // });

  useEffect(() => {
    if (incident) {
      // Parse date and time fields for editing
      let parsedIncident = { ...incident };
      // crime_incidence_time: extract date part (YYYY-MM-DD) from ISO string
      if (parsedIncident.crime_incidence_time) {
        const dateObj = new Date(parsedIncident.crime_incidence_time);
        if (!isNaN(dateObj.getTime())) {
          parsedIncident.crime_incidence_time = dateObj
            .toISOString()
            .split("T")[0];
        }
      }
      // incident_timestamp: extract time part (HH:mm) from ISO string
      if (parsedIncident.incident_timestamp) {
        const timeStr = parsedIncident.incident_timestamp;
        if (timeStr.includes("T")) {
          // ISO string: split and get time
          const tPart = timeStr.split("T")[1];
          if (tPart) {
            parsedIncident.incident_timestamp = tPart.substring(0, 5); // HH:mm
          }
        } else if (timeStr.length > 5) {
          // If time is like HH:mm:ss, trim to HH:mm
          parsedIncident.incident_timestamp = timeStr.substring(0, 5);
        }
      }
      // year: extract year from date string or ISO string
      if (parsedIncident.year) {
        const yearDate = new Date(parsedIncident.year);
        if (!isNaN(yearDate.getTime())) {
          parsedIncident.year = yearDate.getFullYear().toString();
        }
      }
      setFormState(parsedIncident);
    }
  }, [incident]);

  useEffect(() => {
    fetchCrimeReferenceReceptionMethod()
      .then(setReceptionMethods)
      .catch(console.error);

    // fetchCrimeCategoryBaseData().then((data) =>
    //   setBaseData((prev) => ({ ...prev, crimeCategory: data }))
    // );

    // fetchProsecutor().then(setProsecutor).catch(console.error);
    // fetchPolice().then(setPolices).catch(console.error);
  }, []);

  useEffect(() => {
    if (loginData) {
      setFormState((prevState) => ({
        ...prevState,
        // receiving_prosecutor: hasRole(loginData?.roles, [Roles.prosecutor])
        //   ? loginData?.user_id
        //   : null,
        // receiving_police: hasRole(loginData?.roles, [Roles.member])
        //   ? loginData?.user_id
        //   : null,
      }));
    }
  }, [loginData]);

  // const handleFile = (file: File | null) => {
  //   setFormState({ ...formState, crime_narrative_files: file });
  // };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => {
      if (name === "year") {
        // When year is selected, clear date and time
        return {
          ...prevState,
          year: value,
          crime_incidence_time: "",
          incident_timestamp: "",
        };
      } else if (name === "crime_incidence_time") {
        // When date is selected, clear time
        return {
          ...prevState,
          crime_incidence_time: value,
          incident_timestamp: "",
        };
      } else if (name === "incident_timestamp") {
        return {
          ...prevState,
          incident_timestamp: value,
        };
      } else {
        return { ...prevState, [name]: value };
      }
    });
  };

  useEffect(() => {
    if (file) {
      // Map file.crime_type to CrimeReference.crime_type
      let mappedCrimeType: CrimeReference["crime_type"] = null;
      if (file.crime_type === "various") mappedCrimeType = "various_crime";
      else if (file.crime_type === "corruption")
        mappedCrimeType = "corruption_crime";
      else if (file.crime_type === "economic")
        mappedCrimeType = "economic_crime";
      else mappedCrimeType = null;

      setFormState((prevState) => ({
        ...prevState,
        Incident_Registration_number: file.incident_registration_number || null,
        crime_type: mappedCrimeType,
        is_letter: true,
      }));
    }
  }, [file]);

  const validateForm = (tab = "all") => {
    if (tab === "all" || tab === "Crime Information") {
      if (!formState.Incident_Registration_number)
        return "Incident Registration Number is Required.";
      if (!formState.date_received) return "Date Received is Required.";
      else if (!formState.crime_type) return "Crime Type is Required.";
      else if (!formState.description) return "Description is Required.";
    }
    if (tab === "all" || tab === "Incident Information") {
      // if (!formState.crime_incidence_time)
      //   return "Crime Incidence Time is Required.";
      // // else if (!formState.victim_status) return "Victim Status is Required.";
      if (!formState.crime_incident_narrative)
        return "Crime Incidence Narrative is Required.";
    }
    // if (tab === "all" || tab === "Address Information") {
    //   if (!formState.crime_incidence_region)
    //     return "Crime Incidence Region is Required.";
    //   else if (!formState.crime_incidence_zone)
    //     return "Crime Incidence Zone is Required.";
    //   else if (!formState.crime_incidence_city)
    //     return "Crime Incidence City is Required.";
    //   else if (!formState.crime_incidence_woreda)
    //     return "Crime Incidence Woreda is Required.";
    //   else if (!formState.crime_incidence_kebele)
    //     return "Crime Incidence Kebele is Required.";
    //   else if (!formState.crime_incidence_site)
    //     return "Crime Incidence Site is Required.";
    // }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    // Combine date and time into ISO string if both are present
    let combinedIncidentTimestamp = formState.incident_timestamp;
    if (formState.crime_incidence_time && formState.incident_timestamp) {
      // If time is like "14:30", combine with date
      const date = formState.crime_incidence_time;
      const time = formState.incident_timestamp;
      // If time is already in ISO format, don't change
      if (!time.includes("T")) {
        combinedIncidentTimestamp = `${date}T${time.length === 5 ? time + ":00" : time}`;
      }
    }

    // Stamp the registering user's admin unit (CharField `admin_unit_id` on the
    // backend) from the auth context. admin_unit may sit at the top level or
    // under `organization`, mirroring how App.tsx resolves it.
    const adminUnit =
      formState.admin_unit_id ||
      loginData?.admin_unit ||
      loginData?.organization?.admin_unit;

    const submitData = {
      ...formState,
      incident_timestamp: combinedIncidentTimestamp,
      ...(adminUnit ? { admin_unit_id: String(adminUnit) } : {}),
    };

    setLoading(true);
    incident
      ? // If incident exists, update the existing incident
        editCrimeReferences(incident?.uu_id, {
          ...submitData,
          crime_naretive_file:
            typeof formState.crime_naretive_file === "object"
              ? formState.crime_naretive_file
              : undefined,
        })
          .then(() => {
            showTooltip(t("successfully_updated"), "success");
            setTab("Crime Information");
            setFormState({ ...formState, crime_naretive_file: null });
          })
          .catch((err) => {
            showTooltip(errorHandlerOnForm(err?.response?.data), "error");
          })
          .finally(() => setLoading(false))
      : addCrimeReferences(submitData)
          .then((data) => {
            showTooltip(t("successfully_submitted"), "success");
            navigate(
              file ? "/record-incident/" + data?.uu_id : "/incident-list",
              {
                state: { file: file },
              },
            );
          })
          .catch((err) => {
            showTooltip(errorHandlerOnForm(err?.response?.data), "error");
          })
          .finally(() => setLoading(false));
  };

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">📦</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Incident Details Information
              </h1>
              <p className="text-gray-600 mt-1">
                Lorem Ipsum is a placeholder text commonly used in the design...
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white shadow-md rounded-lg p-6 mt-4 ">
          {/* Crime Information */}
          <div>
            <div className="flex items-center mt-4 w-[678px]">
              <p
                className="text-[#9D0303] font-[Poppins] font-[600] text-lg cursor-pointer"
                onClick={() => setTab("Crime Information")}
              >
                Crime Information
              </p>
              <hr className="flex-1 border-t border-[#E9E9E9] ml-4" />
            </div>
            <div className={tab === "Crime Information" ? "block" : "hidden"}>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Incident Registration Number{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="#1234"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    required
                    name="Incident_Registration_number"
                    value={formState?.Incident_Registration_number || ""}
                    onChange={handleChange}
                    readOnly={incident || file ? true : false}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Date Received <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    placeholder="Eg. Sept 12 , 2024"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="date_received"
                    required
                    value={
                      formState?.date_received
                        ? new Date(formState.date_received)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={handleChange}
                  />
                </div>

                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="crime_type"
                    value={formState?.crime_type ?? ""}
                    onChange={handleChange}
                  >
                    <option value={""}>Select Crime Type</option>
                    {Object.entries(CrimeType).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                {/* {!file?.crime_type && (
                  <div className="flex flex-col mt-4">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Crime Type
                    </label>
                    <select
                      className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                      name="crime_type"
                      value={formState?.crime_type ?? ""}
                      onChange={handleChange}
                    >
                      <option value={""}>Select Crime Type</option>
                      {Object.entries(CrimeType).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>
                )} */}

                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Reception Method
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="reception_method"
                    value={formState?.reception_method?.toString() ?? ""}
                    onChange={handleChange}
                  >
                    <option value={""}>Select Reception Method</option>
                    {receptionMethods.map((method, index) => (
                      <option key={index} value={method.uu_id}>
                        {method.reception_method}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    placeholder="Crime Category"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="category"
                    value={formState?.category ?? ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Reference Type
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="reference_type"
                    value={formState?.reference_type || ""}
                    onChange={handleChange}
                  >
                    <option value={""}>Select Reference Type</option>
                    {Object.entries(ReferenceType).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Asset Reference
                  </label>
                  <input
                    type="text"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="asset_reference"
                    value={formState?.asset_reference || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Report Application Number
                  </label>
                  <input
                    type="text"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="report_application_number"
                    value={formState?.report_application_number || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Receiving Prosecutor
                  </label>
                  <input
                    type="text"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="receiving_prosecutor"
                    value={formState?.receiving_prosecutor || ""}
                    onChange={handleChange}
                  />
                  {/* <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="receiving_prosecutor"
                    value={Number(formState?.receiving_prosecutor) ?? ""}
                    onChange={handleChange}
                  >
                    <option value={""}>Select Receiving Prosecutor</option>
                    {prosecutors.map((prosecutors, index) => (
                      <option key={index} value={prosecutors.uu_id}>
                        {prosecutors.full_name}
                      </option>
                    ))}
                  </select> */}
                </div>

                {/* <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Receiving Police
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="receiving_police"
                    value={Number(formState?.receiving_police) ?? ""}
                    onChange={handleChange}
                  >
                    <option value={""}>Select Receiving Police</option>
                    {polices.map((police, index) => (
                      <option key={index} value={police.uu_id}>
                        {police.full_name}
                      </option>
                    ))}
                  </select>
                </div> */}
              </div>

              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Description *
                </label>
                <textarea
                  rows={4}
                  placeholder="Add Here"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="description"
                  value={formState?.description ?? ""}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-row gap-2 justify-end mt-6">
                <button
                  type="button"
                  className="right-0 bg-[#9d030352] text-[#ffffff] px-4 py-1 rounded-[4px]"
                  disabled
                >
                  Back
                </button>
                <button
                  type="button"
                  className="right-0 bg-[#9D0303] text-[#ffffff] px-4 py-1 rounded-[4px] "
                  onClick={() => {
                    const error = validateForm("Crime Information");

                    error
                      ? showTooltip(error, "error")
                      : setTab("Incident Information");
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {/* Incident Information */}
          <div>
            <div className="flex items-center mt-4 w-[678px]">
              <p
                className="text-[#9D0303] font-[Poppins] font-[600] text-lg cursor-pointer"
                onClick={() => {
                  const error = validateForm(tab);
                  error
                    ? showTooltip(error, "error")
                    : setTab("Incident Information");
                }}
              >
                Incident Information
              </p>
              <hr className="flex-1 border-t border-[#E9E9E9] ml-4" />
            </div>
            <div
              className={tab === "Incident Information" ? "block" : "hidden"}
            >
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Incident Time
                  </label>
                  <div className="flex flex-row gap-2">
                    <div className="flex flex-col flex-1">
                      <label className="text-xs text-[#747171] mb-1">
                        Year of Incident
                      </label>
                      <select
                        className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                        name="year"
                        value={
                          formState?.year
                            ? // Extract year from the stored date (handles both Date objects and strings)
                              new Date(formState.year).getFullYear().toString()
                            : ""
                        }
                        onChange={(e) => {
                          const year = e.target.value;
                          // Create a date string for the first day of the selected year
                          const dateValue = year ? `${year}-01-01` : "";
                          // Call the existing handleChange with a synthetic event-like object
                          setFormState((prevState) => ({
                            ...prevState,
                            year: dateValue,
                          }));
                          // handleChange({
                          //   target: {
                          //     name: "year",
                          //     value: dateValue,
                          //   },
                          // });
                        }}
                      >
                        <option value="" disabled>
                          Select year
                        </option>
                        {Array.from(
                          { length: new Date().getFullYear() - 1900 + 1 },
                          (_, i) => {
                            const year = 1900 + i;
                            return (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            );
                          },
                        )}
                      </select>
                    </div>
                    <div className="flex flex-col flex-1">
                      <label className="text-xs text-[#747171] mb-1">
                        Date of Incident
                      </label>
                      <input
                        type="date"
                        className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                        name="crime_incidence_time"
                        placeholder="Date"
                        value={formState?.crime_incidence_time ?? ""}
                        onChange={handleChange}
                        disabled={!formState?.year}
                        min={
                          formState?.year
                            ? `${formState.year}-01-01`
                            : undefined
                        }
                        max={
                          formState?.year
                            ? `${formState.year}-12-31`
                            : undefined
                        }
                      />
                    </div>
                    <div className="flex flex-col flex-1">
                      <label className="text-xs text-[#747171] mb-1">
                        Time of Incident
                      </label>
                      <input
                        type="time"
                        className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                        name="incident_timestamp"
                        placeholder="Time"
                        value={formState?.incident_timestamp ?? ""}
                        onChange={handleChange}
                        disabled={!formState?.crime_incidence_time}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col mt-9">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Victim Status
                  </label>
                  <input
                    type="text"
                    placeholder=""
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="victim_status"
                    value={formState?.victim_status ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Category
                  </label>
                  {/* <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    name="crime_category"
                    value={formState?.crforime_category ?? ""}
                    onChange={handleChange}
                  >
                    <option value={""}>Select Crime Category</option>
                    {baseData?.crimeCategory.map((category, index) => (
                      <option key={index} value={category?.id}>
                        {category?.name}
                      </option>
                    ))}
                  </select> */}
                  <input
                    type="text"
                    placeholder=""
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="crime_category"
                    value={formState?.crime_category ?? ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Tools Used
                  </label>
                  <input
                    type="text"
                    placeholder=""
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="tools_used"
                    value={formState?.tools_used ?? ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Crime Incident Narrative{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  placeholder="Add Here"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="crime_incident_narrative"
                  required
                  value={formState?.crime_incident_narrative ?? ""}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Evidence Written
                </label>
                <textarea
                  rows={4}
                  placeholder="Add Here"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="evidence_written"
                  value={formState?.evidence_written ?? ""}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-row gap-2 justify-end mt-6">
                <button
                  type="button"
                  className="right-0 bg-[#9D0303] text-[#ffffff] px-4 py-1 rounded-[4px] "
                  onClick={() => setTab("Crime Information")}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="right-0 bg-[#9D0303] text-[#ffffff] px-4 py-1 rounded-[4px] "
                  onClick={() => {
                    const error = validateForm("Incident Information");
                    error
                      ? showTooltip(error, "error")
                      : setTab("Address Information");
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div>
            <div className="flex items-center mt-4 w-[678px]">
              <p
                className="text-[#9D0303] font-[Poppins] font-[600] text-lg cursor-pointer"
                onClick={() => {
                  const error = validateForm(tab);
                  error
                    ? showTooltip(error, "error")
                    : setTab("Address Information");
                }}
              >
                Address Information
              </p>
              <hr className="flex-1 border-t border-[#E9E9E9] ml-4" />
            </div>
            <div className={tab === "Address Information" ? "block" : "hidden"}>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Incidence Region
                  </label>
                  <input
                    type="text"
                    placeholder=""
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="crime_incidence_region"
                    value={formState?.crime_incidence_region ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Incidence Zone
                  </label>
                  <input
                    type="text"
                    placeholder=""
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="crime_incidence_zone"
                    value={formState?.crime_incidence_zone ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Incidence City
                  </label>
                  <input
                    type="text"
                    placeholder=""
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="crime_incidence_city"
                    value={formState?.crime_incidence_city ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Incidence Woreda
                  </label>
                  <input
                    type="text"
                    placeholder=""
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="crime_incidence_woreda"
                    value={formState?.crime_incidence_woreda ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Incidence Kebele
                  </label>
                  <input
                    type="text"
                    placeholder=""
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="crime_incidence_kebele"
                    value={formState?.crime_incidence_kebele ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Crime Incidence Site
                  </label>
                  <input
                    type="text"
                    placeholder=""
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="crime_incidence_site"
                    value={formState?.crime_incidence_site ?? ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* <div className="flex flex-row gap-2 justify-end mt-6">
                <button
                  type="button"
                  className="right-0 bg-[#9D0303] text-[#ffffff] px-4 py-1 rounded-[4px] "
                  onClick={() => setTab("Incident Information")}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="right-0 bg-[#9D0303] text-[#ffffff] px-4 py-1 rounded-[4px] "
                  onClick={() => {
                    const error = validateForm("Address Information");
                    error
                      ? showTooltip(error, "error")
                      : setTab("Additional Information");
                  }}
                >
                  Next
                </button>
              </div> */}
              <div className="flex flex-row mt-4 gap-3">
                {!(incident || file) && (
                  <input
                    type="reset"
                    className="border border-[#212121] px-[76px] py-3 text-[#747474] font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#D4D4D4]"
                    value={"Cancel"}
                  />
                )}
                <button
                  disabled={loading}
                  type="submit"
                  className="border border-[#212121] bg-[#212121] px-[76px] py-3 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
                  value={incident || file ? "Save" : "Finish"}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon}
                      Loading...
                    </div>
                  ) : incident || file ? (
                    "Save"
                  ) : (
                    "Finish"
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            {/* <div className="flex items-center mt-4 w-[678px]">
              <p
                className="text-[#9D0303] font-[Poppins] font-[600] text-lg cursor-pointer"
                onClick={() => {
                  const error = validateForm(tab);
                  error
                    ? showTooltip(error, "error")
                    : setTab("Additional Information");
                }}
              >
                Additional Information
              </p>
              <hr className="flex-1 border-t border-[#E9E9E9] ml-4" />
            </div> */}

            {/* <div
              className={tab === "Additional Information" ? "block" : "hidden"}
            >
              <div className="mt-4">
                <FileUpload
                  label="Crime Incident Narrative File"
                  fileTypes="*"
                  required={true}
                  onChange={handleFile}
                />
              </div>
            </div> */}
          </div>

          {/* <div className="flex flex-row mt-4 gap-3">
            {!(incident || file) && (
              <input
                type="reset"
                className="border border-[#212121] px-[76px] py-3 text-[#747474] font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#D4D4D4]"
                value={"Cancel"}
              />
            )}
            <input
              type="submit"
              className="border border-[#212121] bg-[#212121] px-[76px] py-3 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
              value={incident || file ? "Save" : "Finish"}
            />
          </div> */}
        </div>
      </form>
    </div>
  );
};

export default IncidentInformationTab;
